import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Box, Card, Typography } from "@mui/material";
import { useMemo } from "react";
import CustomSelect from "../../../../../components/Select/Select";
import { AppColors } from "../../../../../util/AppColors";
import { canWriteModule } from "../../../../../util/roleModuleAccess";

const PharmacyAdministerCard = ({
  item,
  batchOptions = [],
  onBatchChange,
  onIssue,
  onReject,
  readOnly = false,
}) => {
  const canManagePharmacy = canWriteModule("pharmacy");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicId, locations } = useMemo(() => {
    if (!mandateDataRaw)
      return { clinicStocks: [], clinicId: null, locations: [] };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicId: reqData?.clinic?.id ?? null,
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { clinicStocks: [], clinicId: null, locations: [] };
    }
  }, [mandateDataRaw]);

  const reqStock = useMemo(() => {
    if (!item?.stockId) return null;
    return clinicStocks?.find((stc) => stc?.stockId === item?.stockId) ?? null;
  }, [item?.stockId, clinicStocks]);

  const batchNo = useMemo(() => {
    if (item?.batchNumber?.name) return item.batchNumber.name;
    if (item?.batchNo) return item.batchNo;
    if (!item?.batchId || !reqStock) return null;
    const reqBatch = reqStock?.stockBatch
      ?.filter((btch) => btch?.createdBy === clinicId && btch?.approved)
      ?.find((bat) => bat?.batchId === item?.batchId);
    return reqBatch?.batchNo ?? null;
  }, [item, reqStock, clinicId]);

  const isControlledSubstance = useMemo(() => {
    if (!reqStock) return false;
    const isScheduleH1 =
      (reqStock?.drugSchedule ?? "")?.toLowerCase() === "schedule h1";
    const isNdps =
      reqStock?.ndps === true ||
      reqStock?.ndps === "Y" ||
      reqStock?.ndps === "y";
    return isScheduleH1 || isNdps;
  }, [reqStock]);

  const isAutoConsumed = !!reqStock?.autoConsumed;

  const isIssued =
    item?.itemIssued === true || item?.status === "issued" || item?.isIssued;
  const isRejected =
    !isIssued &&
    (item?.isRejected ||
      item?.itemIssued === false ||
      item?.status === "rejected");
  const isStockUnavailable =
    item?.status === "rejected" &&
    (item?.remarks === "Stock Unavailable" ||
      item?.reason === "Stock Unavailable" ||
      item?.itemIssuedStatus === "Stock Unavailable");
  const isPending =
    !readOnly &&
    item?.isEdit !== false &&
    typeof item?.itemIssued !== "boolean" &&
    !isIssued &&
    !isRejected;

  const selectedBatchId = item?.batchNumber?.value ?? item?.batchId ?? null;
  const canIssue = isAutoConsumed || !!selectedBatchId;

  const selectedBatch = useMemo(() => {
    if (!reqStock) return null;
    if (selectedBatchId) {
      return (
        reqStock?.stockBatch?.find(
          (bat) => String(bat?.batchId) === String(selectedBatchId),
        ) ?? null
      );
    }
    return (
      reqStock?.stockBatch?.find(
        (btch) => btch?.createdBy === clinicId && btch?.approved,
      ) ?? null
    );
  }, [reqStock, selectedBatchId, clinicId]);

  const locationName = useMemo(() => {
    if (item?.batchNumber?.locationName) {
      return item.batchNumber.locationName;
    }
    if (!selectedBatch?.locationId) return null;
    return (
      locations?.find((loc) => loc?.id === selectedBatch?.locationId)
        ?.locationName ?? null
    );
  }, [item, selectedBatch, locations]);

  const routeLabel =
    typeof item?.route === "object"
      ? (item?.route?.name ?? item?.route?.value)
      : item?.route;

  const getDotColor = () => {
    if (isIssued) return "#4caf50";
    if (isStockUnavailable) return "#ff9800";
    if (isRejected) return "#f44336";
    return "#0284c7";
  };

  const displayName = item?.procedureName ?? item?.name ?? "Administered item";
  const quantity = Number(item?.quantity ?? 0);
  const lineAmount = Number(item?.estimatedCost ?? item?.total ?? 0);
  const uomName =
    selectedBatch?.salesUom ??
    selectedBatch?.qtyMapName ??
    selectedBatch?.uomMapName ??
    item?.uomName ??
    "";

  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e0e0e0",
        p: 2,
        mb: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        gap={1.5}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: getDotColor(),
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: "15px", fontWeight: 700, color: "#212121" }}
          >
            {displayName}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography
            sx={{ fontSize: "13px", color: "#616161", fontWeight: 500 }}
          >
            {quantity} {uomName} · ₹{lineAmount.toFixed(2)}
          </Typography>

          {isIssued ? (
            <Typography className="green-back5 white-color status-con fs12 font-medium con-fit">
              Issued
            </Typography>
          ) : null}

          {isRejected ? (
            <Typography
              className={`${
                isStockUnavailable ? "orange-back2" : "red-back2"
              } white-color status-con fs12 font-medium con-fit`}
            >
              {isStockUnavailable ? "Stock Unavailable" : "Rejected"}
            </Typography>
          ) : null}

          {canManagePharmacy && isPending ? (
            <>
              <Typography
                className={`blu-back white-color fs14 font-medium con-fit pv5 ph15 br5 cursor`}
                sx={{ opacity: canIssue ? 1 : 0.5 }}
                onClick={() => {
                  if (!canIssue) return;
                  onIssue?.(item);
                }}
              >
                Issue
              </Typography>
              <Typography
                className="red-back2 white-color fs14 font-medium con-fit pv5 ph15 br5 cursor"
                onClick={() => onReject?.(item)}
              >
                Reject
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        {canManagePharmacy && isPending ? (
          <Box sx={{ minWidth: 180, maxWidth: 240 }}>
            <CustomSelect
              list={batchOptions}
              value={item?.batchNumber}
              search
              isBatchNumber
              handleChange={(e, value) => onBatchChange?.(value)}
              disabled={isAutoConsumed}
            />
          </Box>
        ) : batchNo ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Batch: {batchNo}
          </Box>
        ) : null}

        {locationName ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Location: {locationName}
          </Box>
        ) : null}

        {item?.dosage ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Dose: {item.dosage}
            {selectedBatch?.packageUom ? ` ${selectedBatch.packageUom}` : ""}
          </Box>
        ) : null}

        {routeLabel ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Route: {routeLabel}
          </Box>
        ) : null}

        {item?.billSeparated || item?.billedSeparately ? (
          <Box
            sx={{
              backgroundColor: "#fff8e1",
              color: "#e65100",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #ffe082",
            }}
          >
            {`+ ₹${lineAmount.toFixed(2)} billed separately (Clinic)`}
          </Box>
        ) : null}

        {(isIssued || isRejected) && item?.reason ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Reason: {item.reason}
          </Box>
        ) : null}

        <Box
          sx={{
            backgroundColor:
              item?.linked === "Y" || item?.serviceId ? "#f5f5f5" : "#e3f2fd",
            color:
              item?.linked === "Y" || item?.serviceId
                ? "#616161"
                : AppColors.appPrimary,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.2,
            py: 0.4,
            borderRadius:
              item?.linked === "Y" || item?.serviceId ? "4px" : "12px",
            border:
              item?.linked === "Y" || item?.serviceId
                ? "1px solid #e0e0e0"
                : "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {!(item?.linked === "Y" || item?.serviceId) && (
            <LocalOfferIcon sx={{ fontSize: 13 }} />
          )}
          {item?.linked === "Y" || item?.serviceId
            ? (item?.procedure?.procedureName ?? item?.linkToProcedure)
            : "Not linked"}
        </Box>

        {isControlledSubstance ? (
          <Box
            sx={{
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.3,
              borderRadius: "4px",
              border: "1px solid #ffcdd2",
            }}
          >
            Controlled substance
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default PharmacyAdministerCard;
